import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { XMarkIcon } from '@heroicons/react/24/outline';
import AIReadinessAssessment from './AIReadinessAssessment';

interface AssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(8px);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: ${props => props.isOpen ? fadeIn : ''} 0.3s ease;
`;

const ModalContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  overflow-y: auto;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px;
  animation: ${props => props.isOpen ? slideUp : ''} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(11, 152, 206, 0.1),
    0 0 120px rgba(11, 152, 206, 0.15);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 10px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(11, 152, 206, 0.05);
    border-radius: 5px;
    margin: 20px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, var(--color-accent-primary), var(--color-accent-secondary));
    border-radius: 5px;
    border: 2px solid white;
    
    &:hover {
      background: linear-gradient(180deg, var(--color-accent-secondary), var(--color-accent-primary));
    }
  }
  
  @media (max-width: 768px) {
    width: 95%;
    max-height: 90vh;
    border-radius: 20px;
  }
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.95) 90%, rgba(255,255,255,0) 100%);
  z-index: 10;
  padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-md);
  border-bottom: 1px solid rgba(11, 152, 206, 0.1);
  backdrop-filter: blur(10px);
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--spacing-lg);
  right: var(--spacing-lg);
  z-index: 11;
  background: white;
  border: 2px solid rgba(11, 152, 206, 0.2);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  &:hover {
    background: var(--color-accent-primary);
    border-color: var(--color-accent-primary);
    transform: scale(1.05) rotate(90deg);
    box-shadow: 0 4px 12px rgba(11, 152, 206, 0.3);
    
    svg {
      color: white;
    }
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--text-secondary);
    transition: color 0.2s ease;
  }
`;

const ModalTitle = styled.h2`
  font-size: var(--fs-3xl);
  font-weight: var(--font-weight-bold);
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  padding-right: 60px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: var(--fs-2xl);
  }
`;

const ModalSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: var(--fs-md);
  margin-top: var(--spacing-xs);
  margin-bottom: 0;
`;

const AssessmentModal: React.FC<AssessmentModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Prevent background scroll on iOS
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    }
    
    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Overlay isOpen={isOpen} onClick={onClose}>
      <ModalContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose} aria-label="Close assessment">
            <XMarkIcon />
          </CloseButton>
          <ModalTitle>AI Readiness Assessment</ModalTitle>
          <ModalSubtitle>
            Discover your organization's AI implementation potential in just 2 minutes
          </ModalSubtitle>
        </ModalHeader>
        <AIReadinessAssessment onClose={onClose} />
      </ModalContainer>
    </Overlay>
  );
};

export default AssessmentModal;